import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Autocomplete,
  IconButton,
  LinearProgress,
  Divider,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SuperAdminService, {
  Company,
  PermissionPreview,
  DepartmentPreview,
  MigratePermissionsResult,
  MigrateAllResult,
} from '../../services/SuperAdminService';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SuperAdminLayout from './SuperAdminLayout';

interface SuperAdminPermissionMigrationProps {
  /** Modal içinde gösterildiğinde verilir; geri tuşu yerine kapatma butonu çalışır. */
  onClose?: () => void;
}

const SuperAdminPermissionMigration: React.FC<SuperAdminPermissionMigrationProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const embedded = typeof onClose === 'function';

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const [scanning, setScanning] = useState(false);
  const [permPreview, setPermPreview] = useState<PermissionPreview | null>(null);
  const [deptPreview, setDeptPreview] = useState<DepartmentPreview | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [result, setResult] = useState<MigratePermissionsResult | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Toplu (tüm şirketler) taşıma durumu
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState<MigrateAllResult | null>(null);
  // Canlı ilerleme: hangi şirket / kaçıncı / kaç toplam.
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number; name: string } | null>(null);
  // Canlı log satırları (en yeni üstte).
  const [bulkLog, setBulkLog] = useState<string[]>([]);

  const isSuperAdmin = userDetails?.role === 'superadmin';

  useEffect(() => {
    const loadCompanies = async () => {
      setLoadingCompanies(true);
      setError(null);
      try {
        const list = await SuperAdminService.getInstance().getAllCompanies();
        setCompanies(list);
      } catch (err: any) {
        setError('Şirketler yüklenemedi: ' + (err?.message || 'bilinmeyen hata'));
      } finally {
        setLoadingCompanies(false);
      }
    };
    loadCompanies();
  }, []);

  // Şirket değişince önizleme/sonuçları sıfırla.
  const handleSelectCompany = (company: Company | null) => {
    setSelectedCompany(company);
    setPermPreview(null);
    setDeptPreview(null);
    setResult(null);
    setError(null);
    setSuccess(null);
  };

  const handleScan = async () => {
    if (!selectedCompany) return;
    setScanning(true);
    setError(null);
    setSuccess(null);
    setResult(null);

    // ── TEYİT LOGLARI: verinin gerçekten nereden geldiğini doğrula ──
    const nodeBase = process.env.REACT_APP_API_URL || 'https://gtsapi.mt-teknoloji.com';
    const permUrl = `${nodeBase}/api/superadmin/companies/${selectedCompany.id}/permissions`;
    const deptUrl = `${nodeBase}/api/superadmin/companies/${selectedCompany.id}/departments`;
    console.groupCollapsed(
      `%c[YetkiTaşıma] TARA → ${selectedCompany.name} (${selectedCompany.id})`,
      'color:#1976d2;font-weight:bold'
    );
    console.log('🌐 Kaynak: Node API (gts_node_js) → Firebase RTDB');
    console.log('   ↳ Node, RTDB yollarını okur:');
    console.log('       companies/%s/personnel/{uid}/permissions/*', selectedCompany.id);
    console.log('       companies/%s/wmsScreens/{id}/settings/personnelPermissions/{uid}', selectedCompany.id);
    console.log('       companies/%s/departments', selectedCompany.id);
    console.log('🔗 Yetki uç (GET):', permUrl);
    console.log('🔗 Departman uç (GET):', deptUrl);
    const t0 = performance.now();

    try {
      const svc = SuperAdminService.getInstance();
      const [perm, dept] = await Promise.all([
        svc.getCompanyPermissions(selectedCompany.id),
        svc.getCompanyDepartments(selectedCompany.id),
      ]);

      const elapsed = Math.round(performance.now() - t0);
      // Ham yanıtlar — birebir gözle teyit için
      console.log('⏱️  Süre:', elapsed, 'ms');
      console.log('📦 [permissions] HAM yanıt (data):', perm);
      console.log('   ↳ companyId yanıtta:', perm.companyId, '| beklenen:', selectedCompany.id,
        perm.companyId === selectedCompany.id ? '✅ eşleşiyor' : '❌ EŞLEŞMİYOR!');
      console.log('   ↳ toplam satır:', perm.counts.totalRows,
        '| yetkili personel:', perm.counts.personnelWithPermissions);
      console.table(perm.counts.byType);
      console.log('   ↳ ilk 10 yetki satırı (örnek):', perm.permissions.slice(0, 10));
      // ScreenType bazında ilk örnek (haritalama doğru mu?)
      const samplesByType: Record<string, any> = {};
      for (const row of perm.permissions) {
        if (!samplesByType[row.ScreenType]) samplesByType[row.ScreenType] = row;
      }
      console.log('   ↳ her ScreenType için 1 örnek satır:', samplesByType);
      // GÜVENLİK: tanınmayan yetki tipi raporu (sessiz veri kaybı kontrolü)
      const unknownTypes = (perm as any).unknownTypes || {};
      const unknownKeys = Object.keys(unknownTypes);
      if (unknownKeys.length > 0) {
        console.warn('   ⚠️ TANINMAYAN yetki tipleri (taşınmıyor!):', unknownTypes);
      } else {
        console.log('   ✅ Tanınmayan yetki tipi yok — permissions altındaki her şey kapsamda.');
      }
      console.log('   ↳ yetkili uid listesi:', perm.uids);

      console.log('📦 [departments] HAM yanıt (data):', dept);
      console.log('   ↳ departman sayısı:', dept.count);
      console.log('   ↳ ilk 10 departman (örnek):', dept.departments.slice(0, 10));
      console.groupEnd();

      setPermPreview(perm);
      setDeptPreview(dept);
      setSuccess(
        `Tarama tamamlandı: ${perm.counts.totalRows} yetki satırı, ${perm.counts.personnelWithPermissions} personel, ${dept.count} departman.`
      );
    } catch (err: any) {
      console.error('❌ [YetkiTaşıma] TARA hatası:', err);
      console.log('   ↳ response:', err?.response?.data);
      console.groupEnd();
      setError('Tarama hatası: ' + (err?.message || 'bilinmeyen hata'));
    } finally {
      setScanning(false);
    }
  };

  // Taşıma hatasını okunur hale getirir: hangi adım (yetki/departman/arşiv) + .NET'in gerçek nedeni.
  const describeMigrateError = (err: any): { text: string; status?: number; raw: any } => {
    const data = err?.response?.data;
    const status: number | undefined = data?.status ?? err?.response?.status;
    const failedUrl: string = data?.failedUrl || '';
    let step = '';
    if (failedUrl.includes('permissions/import')) step = 'yetki';
    else if (failedUrl.includes('departments/import')) step = 'departman';
    else if (failedUrl.includes('legacy/import')) step = 'arşiv';
    const detail = data?.dotnetDetail || data?.responseData?.Detail || data?.responseData?.Message || data?.message || err?.message || 'bilinmeyen hata';
    const text = `${step ? `[${step}] ` : ''}${detail}${status ? ` (HTTP ${status})` : ''}`;
    return { text, status, raw: data ?? err };
  };

  const handleMigrate = async () => {
    if (!selectedCompany) return;
    setConfirmOpen(false);
    setMigrating(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await SuperAdminService.getInstance().migratePermissions(selectedCompany.id);
      setResult(res);
      const legacyMsg = res.legacy ? `, arşiv(+${res.legacy.migrated}/~${res.legacy.updated})` : '';
      setSuccess(
        `Taşıma tamamlandı: yetki(+${res.permissions.migrated}/~${res.permissions.updated}), departman(+${res.departments.migrated}/~${res.departments.updated})${legacyMsg}.`
      );
    } catch (err: any) {
      const d = describeMigrateError(err);
      console.error(`❌ [YetkiTaşıma] ${selectedCompany.name} taşıma hatası:`, d.text, d.raw);
      setError('Taşıma hatası: ' + d.text);
    } finally {
      setMigrating(false);
    }
  };

  const handleMigrateAll = async () => {
    setBulkConfirmOpen(false);
    setError(null);
    setSuccess(null);
    setBulkResult(null);
    setBulkLog([]);

    // 1) ÖNCE: kaç şirket taşınacak? (yüklü liste)
    const targets = [...companies];
    if (targets.length === 0) {
      setError('Taşınacak şirket bulunamadı (şirket listesi boş).');
      return;
    }

    setBulkRunning(true);
    setBulkProgress({ current: 0, total: targets.length, name: '' });

    const svc = SuperAdminService.getInstance();
    console.groupCollapsed('%c[YetkiTaşıma] TOPLU TAŞIMA → TÜM ŞİRKETLER', 'color:#9c27b0;font-weight:bold');
    const startLine = `▶️ Başlıyor: ${targets.length} şirket sırayla taşınacak.`;
    console.log(startLine);
    setBulkLog([startLine]);
    const t0 = performance.now();

    // Birikmeli toplamlar + sonuçlar (her şirketten sonra canlı güncellenir).
    const totals = {
      companies: targets.length,
      succeeded: 0,
      failed: 0,
      permMigrated: 0,
      permUpdated: 0,
      permOrphan: 0,
      deptMigrated: 0,
      deptUpdated: 0,
      legacyMigrated: 0,
      legacyUpdated: 0,
    };
    const results: MigrateAllResult['results'] = [];

    // 2) Şirket şirket taşı; her biri bitince log + sayaçlar canlı güncellenir.
    for (let i = 0; i < targets.length; i++) {
      const c = targets[i];
      const idx = i + 1;
      setBulkProgress({ current: idx, total: targets.length, name: c.name });
      const ct0 = performance.now();
      try {
        const r = await svc.migratePermissions(c.id);
        const ms = Math.round(performance.now() - ct0);
        totals.succeeded += 1;
        totals.permMigrated += r.permissions.migrated;
        totals.permUpdated += r.permissions.updated;
        totals.permOrphan += r.permissions.orphan;
        totals.deptMigrated += r.departments.migrated;
        totals.deptUpdated += r.departments.updated;
        totals.legacyMigrated += r.legacy?.migrated ?? 0;
        totals.legacyUpdated += r.legacy?.updated ?? 0;
        results.push({
          companyId: c.id,
          success: true,
          permissions: r.permissions,
          departments: r.departments,
          legacy: r.legacy,
        });
        const line =
          `✅ [${idx}/${targets.length}] ${c.name} — bitti (${ms}ms) | ` +
          `yetki +${r.permissions.migrated}/~${r.permissions.updated} (orphan ${r.permissions.orphan}), ` +
          `dept +${r.departments.migrated}/~${r.departments.updated}, ` +
          `arşiv +${r.legacy?.migrated ?? 0}/~${r.legacy?.updated ?? 0}`;
        console.log(line);
        setBulkLog((prev) => [line, ...prev]);
      } catch (err: any) {
        const d = describeMigrateError(err);
        totals.failed += 1;
        results.push({ companyId: c.id, success: false, error: d.text, status: d.status });
        const line = `❌ [${idx}/${targets.length}] ${c.name} — HATA: ${d.text}`;
        // Tam ham yanıtı konsola yaz (kök neden teşhisi için).
        console.warn(line, d.raw);
        setBulkLog((prev) => [line, ...prev]);
      }
      // Canlı sonuç (kartlar + hata listesi anında güncellensin).
      setBulkResult({ totals: { ...totals }, results: [...results] });
    }

    const elapsed = Math.round(performance.now() - t0);
    const doneLine = `🏁 Tamamlandı: ${totals.succeeded}/${totals.companies} başarılı, ${totals.failed} hata — toplam ${elapsed}ms.`;
    console.log(doneLine);
    console.groupEnd();
    setBulkLog((prev) => [doneLine, ...prev]);
    setBulkProgress(null);
    setSuccess(
      `Toplu taşıma tamamlandı: ${totals.succeeded}/${totals.companies} şirket başarılı, ${totals.failed} hata. ` +
      `Yetki +${totals.permMigrated}/~${totals.permUpdated}, departman +${totals.deptMigrated}/~${totals.deptUpdated}, ` +
      `arşiv +${totals.legacyMigrated}/~${totals.legacyUpdated}.`
    );
    setBulkRunning(false);
  };

  const byTypeEntries = permPreview ? Object.entries(permPreview.counts.byType) : [];
  const orphanCount = result?.permissions.orphan ?? 0;
  const bulkFailed = bulkResult?.results.filter((r) => !r.success) ?? [];

  const content = (
    <Box sx={{ p: embedded ? 3 : 0, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => (embedded ? onClose!() : navigate('/superadmin'))} size="small">
          {embedded ? <CloseIcon /> : <ArrowBackIcon />}
        </IconButton>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon /> Yetki &amp; Departman Taşıma
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Firebase RTDB'deki personel ekran yetkilerini ve departmanları, şirketin kendi SQL veritabanına
        (tenant) taşır. Ayrıca tipli SQL modülü henüz olmayan veriler (şube atamaları, personel finans
        hareketleri, etkinlik/ajanda, bildirim, sözleşme, kullanım istatistiği) kayıpsız JSON olarak
        <strong> arşiv tablosuna</strong> (LegacyMigrationRecords) taşınır. Şirket şirket veya toplu çalışır;
        idempotenttir (tekrar çalıştırılabilir).
      </Typography>

      {/* GEÇİCİ ARAÇ uyarısı */}
      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
        <strong>GEÇİCİ ARAÇ.</strong> Yetkiler hâlâ web'den Firebase'e yazıldığı için cutover sırasında
        (Faz 3) bu araç tekrar çalıştırılmalıdır. Yetki yazımı API'ye (gts_hr_api P5) geçince bu araç
        kaldırılacaktır. Taşınmayan: quickSale/pyk (kanonik kaynağı zaten SQL).
      </Alert>

      {!isSuperAdmin && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Bu işlem yalnızca süperadmin tarafından yapılabilir.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* 0) TOPLU TAŞIMA — tüm şirketler tek seferde (firma firma seçmeden) */}
      <Paper sx={{ p: 2, mb: 3, borderLeft: '4px solid', borderColor: 'secondary.main' }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DoneAllIcon color="secondary" /> Toplu Taşıma (Tüm Şirketler)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {loadingCompanies
            ? 'Şirket listesi yükleniyor…'
            : <>Toplam <strong>{companies.length} şirketin</strong> yetki, departman ve arşiv verisi sırayla taşınır. </>}
          Her şirket bitince ilerleme + sonuç canlı görünür; idempotenttir; bir şirket hata verirse atlanır,
          diğerleri devam eder.
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={bulkRunning ? <CircularProgress size={18} color="inherit" /> : <DoneAllIcon />}
          onClick={() => setBulkConfirmOpen(true)}
          disabled={!isSuperAdmin || bulkRunning || migrating || scanning || loadingCompanies || companies.length === 0}
        >
          {bulkRunning ? 'Tüm şirketler taşınıyor...' : 'Tüm Şirketleri Taşı'}
        </Button>

        {/* Canlı ilerleme */}
        {bulkProgress && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                İşleniyor: {bulkProgress.current}/{bulkProgress.total}
                {bulkProgress.name ? ` — ${bulkProgress.name}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                %{Math.round((bulkProgress.current / Math.max(bulkProgress.total, 1)) * 100)}
              </Typography>
            </Box>
            <LinearProgress
              color="secondary"
              variant="determinate"
              value={(bulkProgress.current / Math.max(bulkProgress.total, 1)) * 100}
            />
          </Box>
        )}

        {/* Canlı log (her şirket bitince satır eklenir) */}
        {bulkLog.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: '#0d1117',
              color: '#c9d1d9',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              maxHeight: 260,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {bulkLog.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </Box>
        )}

        {bulkResult && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>Şirket (Başarılı/Toplam)</Typography>
                    <Typography variant="h5">{bulkResult.totals.succeeded}/{bulkResult.totals.companies}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>Hatalı Şirket</Typography>
                    <Typography variant="h5" color={bulkResult.totals.failed === 0 ? 'text.primary' : 'error'}>
                      {bulkResult.totals.failed}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>Yetki (Eklendi/Güncellendi)</Typography>
                    <Typography variant="h5">+{bulkResult.totals.permMigrated}/~{bulkResult.totals.permUpdated}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>Departman (Eklendi/Güncellendi)</Typography>
                    <Typography variant="h5">+{bulkResult.totals.deptMigrated}/~{bulkResult.totals.deptUpdated}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>Arşiv (Eklendi/Güncellendi)</Typography>
                    <Typography variant="h5">+{bulkResult.totals.legacyMigrated ?? 0}/~{bulkResult.totals.legacyUpdated ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {bulkResult.totals.permOrphan > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Toplam <strong>{bulkResult.totals.permOrphan} orphan uid</strong> bulundu (SystemUsers'ta yok, yazılmadı).
                Kullanıcı taşıması tamamlanınca toplu taşımayı tekrar çalıştırın.
              </Alert>
            )}

            {bulkFailed.length > 0 && (
              <>
                <Alert severity="error" sx={{ mb: 1 }}>
                  {bulkFailed.length} şirket taşınamadı (aşağıda). Düzeltip toplu taşımayı tekrar çalıştırabilirsiniz.
                </Alert>
                <TableContainer sx={{ maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Şirket ID</TableCell>
                        <TableCell>Hata</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkFailed.map((r) => (
                        <TableRow key={r.companyId}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.companyId}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{r.error}{r.status ? ` (HTTP ${r.status})` : ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {bulkResult.totals.failed === 0 && (
              <Alert severity="success">Tüm şirketler başarıyla taşındı.</Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* 1) Şirket seç + Tara */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          1. Şirket Seç (Tek Şirket — İsteğe Bağlı / Önizlemeli)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            sx={{ minWidth: 360, flex: 1 }}
            options={companies}
            loading={loadingCompanies}
            value={selectedCompany}
            onChange={(_e, value) => handleSelectCompany(value)}
            getOptionLabel={(o) => `${o.name} (${o.id})`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Şirket ara / seç"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {loadingCompanies ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            variant="outlined"
            startIcon={scanning ? <CircularProgress size={18} /> : <FindInPageIcon />}
            onClick={handleScan}
            disabled={!selectedCompany || scanning || migrating}
          >
            {scanning ? 'Taranıyor...' : 'Tara (Önizle)'}
          </Button>
        </Box>
      </Paper>

      {/* 2) Önizleme */}
      {permPreview && deptPreview && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            2. Önizleme — {selectedCompany?.name}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Yetki Satırı
                  </Typography>
                  <Typography variant="h4">{permPreview.counts.totalRows}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Yetkili Personel
                  </Typography>
                  <Typography variant="h4">{permPreview.counts.personnelWithPermissions}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Departman
                  </Typography>
                  <Typography variant="h4">{deptPreview.count}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Ekran Tipi
                  </Typography>
                  <Typography variant="h4">{byTypeEntries.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {byTypeEntries.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {byTypeEntries.map(([type, count]) => (
                <Chip key={type} label={`${type}: ${count}`} variant="outlined" color="primary" />
              ))}
            </Box>
          )}

          {permPreview.counts.totalRows === 0 && deptPreview.count === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Bu şirkette taşınacak yetki veya departman bulunamadı.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={migrating ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={
                !isSuperAdmin ||
                migrating ||
                (permPreview.counts.totalRows === 0 && deptPreview.count === 0)
              }
            >
              {migrating ? 'Taşınıyor...' : 'SQL\'e Taşı'}
            </Button>
          </Box>
          {migrating && <LinearProgress sx={{ mt: 2 }} />}
        </Paper>
      )}

      {/* 3) Sonuç */}
      {result && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color={orphanCount === 0 ? 'success' : 'warning'} /> 3. Sonuç
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tür</TableCell>
                  <TableCell align="right">Toplam</TableCell>
                  <TableCell align="right">Eklendi</TableCell>
                  <TableCell align="right">Güncellendi</TableCell>
                  <TableCell align="right">Orphan</TableCell>
                  <TableCell align="right">Hata</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Yetkiler</TableCell>
                  <TableCell align="right">{result.permissions.total}</TableCell>
                  <TableCell align="right">{result.permissions.migrated}</TableCell>
                  <TableCell align="right">{result.permissions.updated}</TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      label={result.permissions.orphan}
                      color={result.permissions.orphan === 0 ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell align="right">{result.permissions.errors}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Departmanlar</TableCell>
                  <TableCell align="right">{result.departments.total}</TableCell>
                  <TableCell align="right">{result.departments.migrated}</TableCell>
                  <TableCell align="right">{result.departments.updated}</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">{result.departments.errors}</TableCell>
                </TableRow>
                {result.legacy && (
                  <TableRow sx={{ '& td': { fontWeight: 600 } }}>
                    <TableCell>Arşiv (toplam)</TableCell>
                    <TableCell align="right">{result.legacy.total}</TableCell>
                    <TableCell align="right">{result.legacy.migrated}</TableCell>
                    <TableCell align="right">{result.legacy.updated}</TableCell>
                    <TableCell align="right">-</TableCell>
                    <TableCell align="right">{result.legacy.errors}</TableCell>
                  </TableRow>
                )}
                {result.legacy &&
                  Object.entries(result.legacy.byType).map(([type, c]) => (
                    <TableRow key={type}>
                      <TableCell sx={{ pl: 4, color: 'text.secondary', fontSize: '0.8rem' }}>↳ {type}</TableCell>
                      <TableCell align="right">{c.total}</TableCell>
                      <TableCell align="right">{c.migrated}</TableCell>
                      <TableCell align="right">{c.updated}</TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">{c.errors}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {orphanCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>{orphanCount} orphan uid</strong> — merkezi SystemUsers'ta bu şirkete bağlı bulunamadı,
              yazılmadı. Önce kullanıcı taşımasını (User Migration) tamamlayıp tekrar deneyin.
              <Box sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.75rem', maxHeight: 160, overflow: 'auto' }}>
                {result.permissions.orphanUids.join(', ')}
              </Box>
            </Alert>
          )}
          {orphanCount === 0 && result.permissions.errors === 0 && result.departments.errors === 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Tüm yetkiler ve departmanlar başarıyla taşındı (orphan=0, hata=0).
            </Alert>
          )}
        </Paper>
      )}

      {/* Onay diyaloğu */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Taşıma Onayı</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            <strong>{selectedCompany?.name}</strong> şirketinin{' '}
            <strong>{permPreview?.counts.totalRows ?? 0}</strong> yetki satırı ve{' '}
            <strong>{deptPreview?.count ?? 0}</strong> departmanı SQL'e taşınacak.
          </Typography>
          <Alert severity="info">
            İşlem idempotenttir; mevcut kayıtlar güncellenir, yenileri eklenir. İstendiğinde tekrar
            çalıştırılabilir.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleMigrate} variant="contained" color="primary" disabled={migrating}>
            {migrating ? <CircularProgress size={22} /> : 'Taşı'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toplu taşıma onay diyaloğu */}
      <Dialog open={bulkConfirmOpen} onClose={() => setBulkConfirmOpen(false)}>
        <DialogTitle>Toplu Taşıma Onayı</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            <strong>{companies.length} şirketin</strong> yetki, departman ve arşiv verileri sırayla SQL'e
            taşınacak. Her şirket bitince ilerleme ve sonuç canlı gösterilir.
          </Typography>
          <Alert severity="info">
            İdempotenttir; mevcut kayıtlar güncellenir, yeniler eklenir. Şirket sayısına göre uzun sürebilir,
            lütfen sayfayı kapatmayın. Hata veren şirketler atlanır ve sonunda listelenir.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleMigrateAll} variant="contained" color="secondary" disabled={bulkRunning}>
            {bulkRunning ? <CircularProgress size={22} /> : 'Tümünü Taşı'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <SuperAdminLayout title="Yetki & Departman Taşıma" icon={<ShieldIcon />}>
      {content}
    </SuperAdminLayout>
  );
};

export default SuperAdminPermissionMigration;
